import Enumeration from '@enums/enum';
import OfferStateEnum, { getOfferState } from '@enums/offer-state-enum';
import sortOffers, { SortModeEnum } from '@utils/offer/sort-offers';

/**
 * The possible groups that we may use to group offers.
 */
export const GroupEnum = new Enumeration({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  EXPIRED: 'expired',
});

/**
 * This function will group the offers by one of the following types:
 *  active, inactive and expire.
 *
 * @param {Array} offers - The offers to process.
 * @param {Object} options - Sorting/ordering options.
 * @returns {Object} - The grouped offers.
 * @example
 *   groupProviders(someEnquiry);
 *   // => { active: [offers], inactive: [offers], expired: [offers]}
 *
 */
export function groupProviders(offers, options = { mode: SortModeEnum.DEFAULT }) {
  const groups = offers
    .map((offer) => addGroupType(offer))
    .reduce(groupByType, {});

  return sort(groups, options);
}

/**
 * Returns a new offer with a group type (active, inactive, expired) field.
 * @private
 */
function addGroupType(offer) {
  return { ...offer, groupType: groupType(offer) };
}

/**
 * Given an offer, will return what's the associated group type.
 *
 * @todo Create proper offer state enumeration.
 * @param {Object} offer - The offer to consider.
 * @return {String} One of: active, inactive, expired
 */
export function groupType(offer) {
  if (getOfferState(offer.status) === OfferStateEnum.EXPIRED) {
    return GroupEnum.EXPIRED;
  }

  if (getOfferState(offer.status) === OfferStateEnum.CLOSED) {
    return GroupEnum.INACTIVE;
  }

  return GroupEnum.ACTIVE;
}

/**
 * Helper function that groups items by `groupType`.
 * @private
 */
function groupByType(groups, item) {
  const val = item.groupType;
  groups[val] = groups[val] || [];
  groups[val].push(item);

  return groups;
}

/**
 * Sorts the offers on the groups-
 *
 * @param {Object} groups - The object with the offer groups.
 * @param {Object} options - Sorting/ordering options.
 * @return {Object} - A new object with the same keys but sorted offers.
 */
function sort(groups, options) {
  return Object.keys(groups).reduce((newGroups, groupKey) => {
    newGroups[groupKey] = sortOffers(groups[groupKey], options);

    return newGroups;
  }, {});
}
